import type { FunnelStage } from '@dataroom/shared';
import type { FolderTier } from '@/api/hooks';

/**
 * A folder's default audience — its tier (ADR-0010 §2) in plain words. One list
 * for "New folder" and "Change audience", so the two never word it differently.
 */
export const FOLDER_AUDIENCES: { value: FolderTier; label: string; hint: string }[] = [
  {
    value: 'teaser',
    label: 'Every invited buyer, before the NDA',
    hint: 'For the teaser. New buyers get it as soon as you invite them.',
  },
  {
    value: 'nda_signed',
    label: 'Buyers who signed the NDA',
    hint: 'For the CIM. Give each buyer access under Access once they reach the CIM.',
  },
  {
    value: 'due_diligence',
    label: 'Only people you give access to',
    hint: 'For due-diligence material. Nobody gets it by default.',
  },
];

/** Every funnel stage in order — mirrors the server's FUNNEL_STAGE_ORDER. */
const STAGE_ORDER: FunnelStage[] = [
  'teaser',
  'nda_signed',
  'cim',
  'loi_submitted',
  'due_diligence',
  'exclusivity_expires',
  'closing',
];

/** Earlier stages reach buyers sooner. */
export function audienceRank(stage: FunnelStage): number {
  return STAGE_ORDER.indexOf(stage);
}

/** Who gets a folder of this tier, in plain words — including the stages "New
 * folder" never offers (a folder tiered some other way). */
export function audienceLabel(stage: FunnelStage): string {
  const listed = FOLDER_AUDIENCES.find((a) => a.value === stage);
  if (listed) return listed.label;
  if (stage === 'cim') return 'Buyers who reached the CIM';
  if (stage === 'loi_submitted') return 'Buyers who submitted an LOI';
  return 'Only people you give access to';
}
